import { FC, useMemo, useState } from "react";
import moment from "moment";
import { Button, Switch, Table, Typography, Modal, message } from 'antd';
import { PlusOutlined, EditOutlined, ExclamationCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import RafflesModal from './RafflesModal';
import { Raffle, Image, Ticket } from "./interfaces";
import RafflesModalEdit from "./RafflesModalEdit";
import { MdConfirmationNumber } from 'react-icons/md';
import { useNavigate } from "react-router-dom";
import { Timestamp, where } from "firebase/firestore";
import { bulkDelete, del, getGenericCollection, update } from "../../services/firestore";
import useOnSnapshot, { PropsUseOnSnapshot } from "../../hooks/useOnSnapshot";

const { confirm } = Modal;

const Raffles: FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [raffle, setRaffle] = useState<Raffle | null>(null);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const propsUseOnSnapshot = useMemo<PropsUseOnSnapshot>(() => {
    return {
      path: "raffles",
      queryConstraint: []
    };
  }, []);
  const { data: raffles, loading } = useOnSnapshot<Raffle>(propsUseOnSnapshot);

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Fecha de cierre',
      dataIndex: 'finalDate',
      key: 'finalDate',
      render: (finalDate: Timestamp) => <Typography.Text>{moment(finalDate.toDate()).format("DD/MM/YYYY hh:mm a")}</Typography.Text>,
    },
    {
      title: 'Imagen principal',
      dataIndex: 'image',
      key: 'image',
      render: (image: Image) => <img src={image.imageUrl} alt="imagenCarro" height={100} style={{ borderRadius: 30 }} />,
    },
    {
      title: 'Activa',
      dataIndex: 'active',
      key: 'edit',
      render: (_: any, item: Raffle) => <Switch checked={item.active} onChange={async () => {
        const activeRaffle = raffles.find(r => r.active);

        if (activeRaffle) {
          await update("raffles", activeRaffle.id, { active: false });
        }

        await update("raffles", item.id, { active: !item.active });
      }} />,
    },
    {
      title: 'Boletos',
      dataIndex: 'tickets',
      key: 'edit',
      render: (_: any, item: Raffle) => <Button
        onClick={() => navigate("/boletos/" + item.id)}
        icon={<MdConfirmationNumber />}
      />,
    },
    {
      title: 'Editar',
      dataIndex: 'edit',
      key: 'edit',
      render: (_: any, item: Raffle) => <Button
        onClick={() => {
          setOpenEdit(true);
          setRaffle(item);
        }}
        icon={<EditOutlined />}
        type="primary"
      />,
    },
    {
      title: 'Eliminar',
      dataIndex: 'delete',
      key: 'delete',
      render: (_: any, item: Raffle) => <Button
        onClick={() => {
          setOpenDelete(true);
          showConfirm(item);
        }}
        icon={<DeleteOutlined />}
      />
    }
  ];

  function showConfirm(raffle: Raffle) {
    confirm({
      visible: openDelete,
      title: '¿Seguro que desea borrar este Rifa?',
      icon: <ExclamationCircleOutlined />,
      content: '',
      okText: "Aceptar",
      cancelText: "Cancelar",
      async onOk() {
        if (deleting) return;

        try {
          setDeleting(true);

          const tickes = await getGenericCollection<Ticket>("tickets", [where("raffleId", "==", raffle.id)]);

          await bulkDelete("tickets", tickes.map(t => t.id));

          await del("raffles", raffle.id!);

          message.success("Rifa eliminada con exito");
        } catch (error) {
          console.log(error);
        } finally {
          setDeleting(false);
          setOpenDelete(false);
        }
      },
    });
  }

  return (
    <div style={{ padding: 30 }}>
      <div style={{ width: "100%" }}>
        <Button
          style={{ float: "right", marginBottom: 20 }}
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setOpen(true)}
        >
          Agregar Rifa
        </Button>
      </div>
      <Table rowKey="id" dataSource={raffles} columns={columns} style={{ overflowX: "auto" }} loading={loading} />
      <RafflesModal
        open={open}
        onClose={() => setOpen(false)}
      />
      <RafflesModalEdit
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        raffleProp={raffle}
      />
    </div >
  );
};

export default Raffles;
